// 9fbef606107a605d69c0edbcd8029e5d
import logger from '@tcp/core/src/utils/loggerInstance';
import endpoints from '@tcp/core/src/services/endpoints';
import { executeExternalAPICall } from '@tcp/core/src/services/handler';
import mediaQuickView from '@tcp/core/src/services/abstractors/mediaQuickView';
import { getStyliticsUserName, getApiTimeOut } from '@tcp/core/src/utils/utils';

const Abstractor = {
  getData: async (params) => {
    try {
      const { isRedirectToPDP, uniqueKey } = params;
      const response = await Abstractor.fetchStyliticsData(params);
      if (!response) return [];

      const styliticsData = response.body;

      if (isRedirectToPDP) {
        return Abstractor.processStyliticsData(styliticsData, uniqueKey);
      }

      const productIds = Abstractor.extractProductIds(styliticsData);
      const inventoryResponse = await mediaQuickView.getData({ productIds });

      return Abstractor.processData(styliticsData, inventoryResponse?.products || {}, uniqueKey);
    } catch (error) {
      Abstractor.handleError(error);
      return [];
    }
  },

  fetchStyliticsData: async ({ count = 1, tags, uniqueKey }) => {
    const {
      getStyliticsProductsByTag: { method, URI, clientTimeout, serverTimeout },
    } = endpoints;

    const payload = {
      body: {
        username: getStyliticsUserName(),
        min: 1,
        total: count,
        tags,
      },
      webService: {
        method,
        URI,
        reqTimeout: getApiTimeOut({ serverTimeout, clientTimeout }),
      },
    };

    const response = await executeExternalAPICall(payload);
    if (
      !response?.body ||
      !Array.isArray(response.body) ||
      response.body.some((item) => !item.items)
    ) {
      logger.error(
        `Stylitics API returned an empty or malformed response for ${uniqueKey}`,
        response
      );
      return null;
    }
    return response;
  },

  extractProductIds: (items) => {
    return items.flatMap((item) => item.items.map(({ remote_id: remoteId }) => remoteId));
  },

  processData: (styliticsData, inventoryData) => {
    return styliticsData.map((item) => Abstractor.processItem(inventoryData)(item));
  },

  processStyliticsData: (styliticsData) => {
    return styliticsData.map(({ large_image_url: largeImageUrl, id, items: subItems }) => {
      const subItemsId = Abstractor.getSubItemsId(subItems);
      return {
        id,
        largeImageUrl,
        subItemsId,
        items: subItems,
      };
    });
  },

  processItem:
    (inventoryData) =>
    ({ id, items: subItems = [], large_image_url: largeImageUrl = '' }) => {
      const items = Abstractor.mapSubItems(subItems, inventoryData);
      const outOfStockItems = subItems
        .filter(({ remote_id: remoteId }) => !inventoryData[remoteId]?.inventoryCount)
        .map(({ remote_id: remoteId }) => remoteId)
        .join(', ');

      return {
        id,
        largeImageUrl,
        items,
        isOutOfStock: items.length === 0,
        outOfStockItemsId: outOfStockItems,
      };
    },

  mapSubItems: (subItems, inventoryData = {}) => {
    const subItemsId = Abstractor.getSubItemsId(subItems);
    const mappedItems = subItems.map(
      ({
        small_image_url: smallImageUrl,
        name,
        remote_id: remoteId,
        price: productPrice,
        sale_price: discountedPrice,
        brand,
      }) => ({
        smallImageUrl,
        name,
        remoteId,
        subItemsId,
        productPrice,
        discountedPrice,
        inventoryCount: inventoryData?.[remoteId]?.inventoryCount || 0,
        brand,
      })
    );

    return inventoryData ? mappedItems.filter((item) => item.inventoryCount > 0) : mappedItems;
  },

  getSubItemsId: (subItems) => subItems.map(({ remote_id: remoteId }) => remoteId).join('-'),

  handleError: (e) => {
    logger.error('Error in Shop by Look Stylitics Abstractor', e);
  },
};

export default Abstractor;
